import { useGoogleLogin } from "@react-oauth/google";
import { GoogleButton } from "./buttons";
import { captureException } from "src/lib/monitoring";
import { Column, Tooltip, useToast } from "@hightouch/htui";

type Props = {
  action: "Log in" | "Sign up";
  partnerConnectionUUID?: string;
  onSuccess: (user: { welcome?: boolean }) => void;
};

const EnvAwareGoogleIdentityButton = ({
  action,
  partnerConnectionUUID,
  onSuccess,
}: Props) => {
  // Given Vercel's preview URL randomness and Google OAuth not supporting wildcard
  // urls, for now we just link to production for vercel deployments
  return import.meta.env.VITE_VERCEL_ENV === "preview" ? (
    <Tooltip
      message="On preview deployments, this will take you to production in a new tab. Sign in with Google there, then return here and refresh."
      placement="top"
    >
      <Column>
        <GoogleButton
          action={action}
          onClick={() => {
            window.open("https://app.hightouch.com/login");
          }}
        />
      </Column>
    </Tooltip>
  ) : (
    <GoogleIdentityButton
      action={action}
      partnerConnectionUUID={partnerConnectionUUID}
      onSuccess={onSuccess}
    />
  );
};

type GoogleIdentityButtonProps = {
  action: "Log in" | "Sign up";
  partnerConnectionUUID?: string;
  onSuccess: (user: { welcome?: boolean }) => void;
};

const GoogleIdentityButton = ({
  action,
  partnerConnectionUUID,
  onSuccess,
}: GoogleIdentityButtonProps) => {
  const { toast } = useToast();

  const handleError = () => {
    toast({
      id: "google-oauth-failed",
      title: "Failed to authenticate with Google",
      variant: "error",
    });
  };

  const handleSuccess = async (credential: { code: string }) => {
    try {
      const body = { ...credential, partnerConnectionUUID };

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/google/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          credentials: "include",
        },
      );

      if (response.ok) {
        const json = await response.json();
        onSuccess(json.user);
      } else {
        handleError();
      }
    } catch (error) {
      captureException(error);
      handleError();
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (res) => handleSuccess({ code: res.code }),
    onError: handleError,
    scope: "email profile openid",
    include_granted_scopes: false,
  });

  return <GoogleButton onClick={googleLogin} action={action} />;
};

export default EnvAwareGoogleIdentityButton;
